import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { X } from 'lucide-react';
import apiClient from '../services/api';

const COLORS = ['#dc2626', '#f59e0b', '#0891b2', '#7c3aed', '#059669', '#db2777', '#0284c7'];

const DetailModal = ({ bucket, granularity, onClose }) => {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    apiClient.get('/security/cognitive-stats/detail', { params: { bucket, granularity } })
      .then(r => setDetail(r.data))
      .catch(() => setDetail({ error: 'Erreur chargement' }));
  }, [bucket, granularity]);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="attack-detail-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Detail des attaques - {bucket}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>
        {!detail ? <p className="text-sm text-slate-500">Chargement...</p> : detail.error ? <p className="text-sm text-red-600">{detail.error}</p> : (
          <>
            <p className="text-sm text-slate-600 mb-4">{detail.total} blocages sur cette periode</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2">Top IPs</h4>
                {detail.by_ip.length === 0 ? <p className="text-xs text-slate-400">Aucune</p> : (
                  <ul className="space-y-1">
                    {detail.by_ip.map(x => (
                      <li key={x.ip} className="flex justify-between text-xs font-mono">
                        <span>{x.ip}</span><span className="text-slate-500">{x.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2">Par raison</h4>
                <ul className="space-y-1">
                  {detail.by_reason.map(x => (
                    <li key={x.reason} className="flex justify-between text-xs">
                      <span className="text-slate-700">{x.reason}</span><span className="text-slate-500">{x.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {detail.events?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2">Derniers evenements</h4>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                  {detail.events.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 py-1 border-b border-slate-100">
                      <span className="text-slate-400 tabular-nums">{new Date(e.createdAt).toLocaleTimeString('fr-FR')}</span>
                      <span className="font-mono text-slate-700">{e.ip_hash}</span>
                      <span className="text-slate-500">{e.reason}</span>
                      <span className="text-red-600 ml-auto">TTL {Math.round((e.ttl_seconds||0)/60)}min</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const AttacksTimeline = () => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(24);
  const [loading, setLoading] = useState(true);
  const [detailBucket, setDetailBucket] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await apiClient.get('/security/cognitive-stats/timeline', { params: { hours: range } });
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="text-sm text-slate-500">Chargement...</div>;
  if (!data) return <div className="text-sm text-slate-500">Aucune donnee</div>;

  const shortLabel = (b) => {
    if (b.includes('T')) return b.slice(11, 13) + 'h';
    const [, m, d] = b.split('-');
    return `${d}/${m}`;
  };

  const onPointClick = (e) => {
    // e est l'evenement recharts, on cherche le payload.bucket
    const p = e?.activePayload?.[0]?.payload;
    if (p?.bucket) setDetailBucket(p.bucket);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="attacks-timeline">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Timeline attaques bloquees</h3>
          <p className="text-xs text-slate-500 mt-0.5">{data.total_events} evenements sur les {data.range_hours}h - Cliquez sur un point pour le detail</p>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {[24, 72, 168].map((h) => (
            <button
              key={h}
              onClick={() => setRange(h)}
              className={`px-2 py-1 rounded ${range === h ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600'}`}
              data-testid={`timeline-range-${h}`}
            >
              {h === 24 ? '24h' : h === 72 ? '3j' : '7j'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={data.series.map(s => ({ ...s, label: shortLabel(s.bucket) }))} onClick={onPointClick}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="total" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6, cursor: 'pointer' }} name="Total" />
            {(data.reasons || []).map((r, i) => (
              <Line
                key={r}
                type="monotone"
                dataKey={r}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={1.5}
                dot={false}
                name={r}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {detailBucket && (
        <DetailModal
          bucket={detailBucket}
          granularity={data.granularity}
          onClose={() => setDetailBucket(null)}
        />
      )}
    </div>
  );
};

export default AttacksTimeline;
